import React, { ReactElement, useState } from 'react';

import { MenuElement } from '../menu.model';
import { SideMenuLinkContainer, SideMenuSection } from '../sideMenu';
import { TooltipComponent } from '../../tooltip';

export const renderMenuSection = (
  { title, link, icon, children }: MenuElement,
  isOpen: boolean,
  handleOpen: (open: boolean) => void,
  path: string,
  isCollapsed?: boolean,
  handleCollapse?: () => void
): ReactElement => {
  return (
    <SideMenuSection
      key={title}
      link={link}
      icon={icon}
      title={title}
      isOpen={isOpen}
      handleOpen={handleOpen}
      handleCollapse={handleCollapse}
      isCollapsed={isCollapsed}
    >
      {!!children
        ? children?.map((child: MenuElement) => (
            <MenuElementComponent
              key={child.title}
              menuElement={child}
              path={path}
              isCollapsed={isCollapsed}
              handleCollapse={handleCollapse}
            />
          ))
        : null}
    </SideMenuSection>
  );
};

export const renderMenuLink = (
  { title, link, icon }: MenuElement,
  path: string,
  isCollapsed?: boolean
): ReactElement => {
  return (
    <SideMenuLinkContainer
      key={title}
      isActive={link === path}
      nested={true}
      link={link}
      icon={icon}
      title={title}
      isCollapsed={isCollapsed}
    />
  );
};

type MenuElementComponentModel = {
  menuElement: MenuElement;
  path: string;
  isCollapsed?: boolean;
  handleCollapse?: () => void;
};

export const MenuElementComponent = ({
  menuElement,
  path,
  isCollapsed,
  handleCollapse,
}: MenuElementComponentModel): ReactElement => {
  const [isOpened, setOpened] = useState<boolean>(
    !!(
      menuElement.children?.length &&
      menuElement.children?.some((child) => child.link === path)
    )
  );

  const handleOpen = (open: boolean) => {
    setOpened(open);
  };

  if (menuElement.children && menuElement.children.length) {
    if (isCollapsed) {
      return (
        <TooltipComponent mode="dark" title={menuElement.title}>
          {renderMenuSection(
            menuElement,
            isOpened,
            handleOpen,
            path,
            true,
            handleCollapse
          )}
        </TooltipComponent>
      );
    }

    return renderMenuSection(menuElement, isOpened, handleOpen, path);
  }

  return !isCollapsed ? renderMenuLink(menuElement, path, isCollapsed) : <></>;
};

export interface NestedSideMenuProps {
  links: MenuElement[];
  path: string;
  isCollapsed?: boolean;
  handleCollapse: () => void;
}

export const NestedSideMenuContainer = ({
  links,
  path,
  isCollapsed,
  handleCollapse,
}: NestedSideMenuProps): ReactElement => {
  return (
    <>
      {links.map((menuElement: MenuElement) => (
        <MenuElementComponent
          key={menuElement.title}
          menuElement={menuElement}
          path={path}
          isCollapsed={isCollapsed}
          handleCollapse={handleCollapse}
        />
      ))}
    </>
  );
};
